// React import
import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useDrag, useDrop } from 'react-dnd'

// General imports
import _ from 'lodash';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

// MUI imports
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Popover from '@mui/material/Popover';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { green, red, grey, amber } from '@mui/material/colors';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare, faSquare, faArrowUpRightFromSquare, faCheck, faXmark, faUser, faUserPen, faHandHoldingHeart } from '@fortawesome/pro-light-svg-icons';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';

// Local imports
import PIPELINE from './AccountFunnelPipeline.js';
import COLORS from './AccountFunnelPipeline.colors.js';
import { getScoreColor, getScoreExplanation } from '../utils/accountScore.js';
import OwnerFilter from '../components/OpportunitiesFilter.owner.js';

dayjs.extend(duration)
dayjs.extend(relativeTime)

function AccountsMainKanban(props) {

    const { accounts, opportunities, onOpen, updateAccount, org, me, showOverdue, setShowOverdue, sorting: parentSorting, accountScores = {}, accountScorer, hideNewStage = false } = props;
    const [ maxCards, setMaxCards ] = useState(_.fromPairs(PIPELINE.map(p => ([p, 100]))));
    const [ saving, setSaving ] = useState({});
    const [ dragOrder, setDragOrder ] = useState([]);
    const kanbanRef = useRef(null);

    useEffect(() => { kanbanRef.current?.focus(); }, []);

    const onKanbanKeyDown = useCallback((e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            kanbanRef.current?.scrollBy({ left: e.key === 'ArrowRight' ? 300 : -300, behavior: 'smooth' });
        }
    }, []);

    useEffect(() => {
        setDragOrder(accounts.map(a => a.id));
    }, [ accounts ]);

    const sortedAccounts = useMemo(() => {
        // If parent has a sort applied, use parent order (accounts are already sorted)
        // Only use dragOrder for drag-and-drop repositioning within a column
        const accountsMap = _.keyBy(accounts, 'id');
        return _.compact(dragOrder.map(id => accountsMap[id]));
    }, [ dragOrder, accounts ]);
 
    async function onUpdateAccount({ account, pipeline, nextAccountId }) {

        setSaving({ ...saving, [account.id]: 'working' });

        const newAccount = _.cloneDeep(account);
        newAccount.tags = [...(newAccount.tags || []).filter(t => !t.label.startsWith('Pipeline:')), { label: `Pipeline: ${pipeline}` }];

        setDragOrder(_order => {
            const _s = _order.filter(id => id !== account.id);
            const index = !nextAccountId ? _s.length : _s.findIndex(id => id === nextAccountId);
            _s.splice(index, 0, account.id);
            return _s;
        });

        try {
            await updateAccount({ account: newAccount, setFirst: true });
        } catch (e) {
            console.log(e);
            setSaving({ ...saving, [account.id]: 'error' });
            return;
        }
        setSaving({ ...saving, [account.id]: 'success' });
        await new Promise(res => setTimeout(res, 3000));
        setSaving({ ...saving, [account.id]: null });
    }

    const board = {
        lanes: PIPELINE.map((title, index) => {
            const isHiddenNewStage = hideNewStage && title === PIPELINE[0];
            const cards = isHiddenNewStage ? [] : sortedAccounts.filter(a => {
                const pipeline = (a.tags || []).find(t => t.label.startsWith('Pipeline:'));
                if (!pipeline) return index === 0;
                return pipeline.label === `Pipeline: ${title}`;
            });
            return {
                id: title,
                title: `${title} (${cards.length})`,
                cards: _.slice(cards, 0, maxCards[title]).map((a, index2) => ({
                    id: `${index}-${index2}`,
                    account: a,
                    title: a.name
                })),
                hasMore: cards.length > maxCards[title],
                hiddenMessage: isHiddenNewStage ? "Hidden, use toggle above to show" : null,
            };
        })
    };

    return (
        <Box ref={kanbanRef} tabIndex={0} onKeyDown={onKanbanKeyDown} sx={{ overflowX: 'auto', outline: 'none' }}>
        <Board
            disableCardDrag={false}
            onCardDragEnd={(board, source, destination, card) => {
                console.log('onCardDragEnd', board, source, destination, card);
            }}
            renderColumnHeader={({ id, index, title }) => {
                const count = title.match(/\((\d+)\)/)?.[1] || '0';
                const name = title.replace(/\s*\(\d+\)/, '');
                return (
                    <Box sx={{ background: COLORS[index], borderRadius: '8px', padding: '8px 14px', minWidth: '250px', maxWidth: '250px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#fff' }}>{name}</Typography>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '10px', px: '8px', py: '1px' }}>{count}</Typography>
                    </Box>
                );
            }}
            renderCard={({ account }) => {
                const accountOpportunities = opportunities.filter(o => o.account.id === account.id);
                return <Card account={account} opportunities={accountOpportunities} onOpen={onOpen} onDrop={onUpdateAccount} updateAccount={updateAccount} saving={saving[account.id]} org={org} me={me} showOverdue={showOverdue} score={accountScores[account.id] || (accountScorer ? accountScorer(account) : null)} />;
            }}
            data={board}
            onDrop={onUpdateAccount}
            setMaxCards={setMaxCards}
        />
        </Box>
    );
}

export default AccountsMainKanban;

function Board(props) {
    const { disableCardDrag, onCardDragEnd, renderColumnHeader, renderCard, data, onDrop, setMaxCards } = props;

    return (
        <Box sx={{ display: 'flex', width: `calc(268px * ${data.lanes.length} + 30px)` }}>
            {data.lanes.map((lane, index) => (
                <Column
                    index={index}
                    key={lane.id}
                    data={lane}
                    renderColumnHeader={renderColumnHeader}
                    renderCard={renderCard}
                    onDrop={({ account }) => onDrop({ account, pipeline: lane.id })}
                    onShowMore={() => setMaxCards(maxCards => ({ ...maxCards, [lane.id]: maxCards[lane.id] + 100 }))}
                />
            ))}
        </Box>
    );
}

function Column(props) {
    const { data, renderCard, renderColumnHeader, onDrop, index, onShowMore } = props;

    const [isDropping, setIsDropping] = useState(false);
    const first = useRef();
    const _firstCard = useRef();
    _firstCard.current = data.cards[0];
    const _setIsDropping = useRef(null);
    _setIsDropping.current = setIsDropping;
    const _onDrop = useRef(null);
    _onDrop.current = onDrop;

    const [ , drop ] = useDrop(() => ({ 
        accept: ['AccountKanbanCard'],
        hover: (item, monitor) => {
            if (first.current) return;
            first.current = true;
            if (monitor.isOver({ shallow: true })) {
                _setIsDropping.current(monitor.canDrop(item, monitor));
            }
            function isOver() {
                if (monitor.isOver()) {
                    if (monitor.isOver({ shallow: true })) {
                        _setIsDropping.current(monitor.canDrop(item, monitor));
                    } else {
                        _setIsDropping.current(null);
                    }
                    setTimeout(isOver, 100);
                } else {
                    first.current = false;
                    _setIsDropping.current(null);
                }
            }
            setTimeout(isOver, 100);
        },
        drop: (item, monitor) => {
            _onDrop.current({ account: item.account, pipeline: data.id });
        },
        canDrop: (item, monitor) => {
            const { account } = item;
            if (monitor.isOver({ shallow: true }) !== monitor.isOver()) return false;

            if (!_firstCard.current) return true;

            const pipeline = ((account.tags || []).find(t => t.label.startsWith(`Pipeline:`)) || {}).label || `Pipeline: ${PIPELINE[0]}`;
            const _pipeline = ((_firstCard.current.account.tags || []).find(t => t.label.startsWith(`Pipeline:`)) || {}).label || `Pipeline: ${PIPELINE[0]}`;

            if (pipeline === _pipeline) return false;

            return true;
        },
    }));

    return (
        <Box ref={drop} sx={{ flex: 1, display: 'flex', flexDirection: 'column', width: '262px', minWidth: '262px', maxWidth: '262px', marginRight: '6px', border: '1px solid #eee', padding: '6px' }}>
            {renderColumnHeader({ id: data.id, title: data.title, index })}
            {data.hiddenMessage ? (
                <Box sx={{ width: '250px', minWidth: '250px', maxWidth: '250px', mt: 1, p: 1.5, textAlign: 'center', color: '#94a3b8', fontSize: '11px', fontStyle: 'italic' }}>
                    {data.hiddenMessage}
                </Box>
            ) : (
                <>
                    {data.cards.map(card => (
                        <Box key={card.id} sx={{ display: 'flex', flexDirection: 'column', width: '250px', minWidth: '250px', maxWidth: '250px' }}>
                            {renderCard(card)}
                        </Box>
                    ))}
                    {data.hasMore && (
                        <Button
                            sx={{ width: '250px', minWidth: '250px', maxWidth: '250px', mt: 1, textTransform: 'none' }}
                            variant='text'
                            onClick={onShowMore}
                        >
                            Show more...
                        </Button>
                    )}
                </>
            )}
            {isDropping && <Box sx={{ flex: 1, background: 'rgba(0,0,0,0.05)' }} />}
        </Box>
    );
}

function Card(props) {
    const { account, opportunities, onOpen, onDrop, updateAccount, saving, org, me, showOverdue, score } = props;
    const { name, reminders, tags } = account;
    const [isDragging, setIsDragging] = useState(false);
    const [isDropping, setIsDropping] = useState(false);
    const [ownerAnchor, setOwnerAnchor] = useState(null);
    const [changeOwnerDialog, setChangeOwnerDialog] = useState(false);
    const [newOwner, setNewOwner] = useState(null);
    const [ownerSaving, setOwnerSaving] = useState(false);

    const ownerShort = (account.owner || '').split('@')[0];
    const isOwner = account.owner === me?.email;

    async function onTakeOwnership() {
        setOwnerAnchor(null);
        setOwnerSaving(true);
        try {
            await updateAccount({ account: { ...account, owner: me.email }, setFirst: true, includeOwner: true });
        } catch (e) {
            console.log('Error taking ownership:', e);
        }
        setOwnerSaving(false);
    }

    async function onChangeOwnership() {
        setOwnerSaving(true);
        try {
            await updateAccount({ account: { ...account, owner: newOwner }, setFirst: true, includeOwner: true });
            setChangeOwnerDialog(false);
        } catch (e) {
            console.log('Error changing ownership:', e);
        }
        setOwnerSaving(false);
    }
    const first = useRef();
    const _account = useRef(null);
    _account.current = account;
    const _setIsDragging = useRef(null);
    _setIsDragging.current = setIsDragging;
    const _setIsDropping = useRef(null);
    _setIsDropping.current = setIsDropping;
    const _onDrop = useRef(null);
    _onDrop.current = onDrop;

    const [, drag] = useDrag(() => ({
        type: 'AccountKanbanCard',
        item: () => {
            _setIsDragging.current(true);
            return { account: _account.current };
        },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging()
        }),
        end: () => {
            _setIsDragging.current(false);
        },
    }));

    const [ , drop ] = useDrop(() => ({ 
        accept: ['AccountKanbanCard'],
        hover: (item, monitor) => {
            if (first.current) return;
            first.current = true;
            _setIsDropping.current(monitor.canDrop(item, monitor));
            function isOver() {
                if (monitor.isOver()) {
                    setTimeout(isOver, 100);
                } else {
                    first.current = false;
                    _setIsDropping.current(null);
                }
            }
            setTimeout(isOver, 100);
        },
        drop: (item, monitor) => {
            const _pipeline = ((_account.current.tags || []).find(t => t.label.startsWith(`Pipeline:`)) || {}).label || `Pipeline: ${PIPELINE[0]}`;
            _onDrop.current({ account: item.account, pipeline: _pipeline.substr(10), nextAccountId: _account.current.id});
        },
        canDrop: (item, monitor) => {
            const { account } = item;

            const pipeline = ((account.tags || []).find(t => t.label.startsWith(`Pipeline:`)) || {}).label || `Pipeline: ${PIPELINE[0]}`;
            const _pipeline = ((_account.current.tags || []).find(t => t.label.startsWith(`Pipeline:`)) || {}).label || `Pipeline: ${PIPELINE[0]}`;

            if (pipeline === _pipeline) return false;

            return true;
        },
    }));

    const lastContract = _(opportunities).
        filter(o => o.type === 'contract').
        maxBy(o => o.created);

    const lastOneOff = _(opportunities).
        filter(o => o.type === 'one-off').
        maxBy(o => o.created);

    const nonCwUpdates = (account.updates || []).filter(u => !u.html?.includes('Account updated in Cargowise'));
    const lastUpdate = _(nonCwUpdates).maxBy(o => o.created);

    // Activity color — same logic as list view
    const lastNonCwUpdate = _(nonCwUpdates).maxBy(o => o.created);
    const lastActivityDate = _.max(_.compact([
        lastNonCwUpdate?.created,
        lastContract?.created,
        lastOneOff?.created,
        ...(account.reminders || []).filter(r => r.status === 'completed').map(r => new Date(r.dueDate).getTime()),
    ]));
    const daysSinceActivity = lastActivityDate ? dayjs().startOf('day').diff(dayjs(lastActivityDate).startOf('day'), 'day') : null;
    const activityColor = daysSinceActivity === null ? grey[400] : daysSinceActivity <= 7 ? green[600] : daysSinceActivity <= 30 ? amber[700] : red[500];
    const activityLabel = daysSinceActivity === null ? 'No activity' : daysSinceActivity === 0 ? '● Active today' : daysSinceActivity <= 7 ? `● Active ${daysSinceActivity}d ago` : `● Last activity ${daysSinceActivity}d ago`;

    return (
        <Box ref={drop}>
            {isDropping && (
                <Box sx={{ border: '1px dashed #ccc', paddingTop: '150px', width: '100%', mb: 1, mt: 1, background: 'rgba(0,0,0,0.05)' }} />
            )}
            <Paper ref={drag} sx={{
                mb: 1, mt: 1, p: 1.5, width: '250px', minWidth: '250px', maxWidth: '250px',
                opacity: isDragging ? 0.1 : 1, borderRadius: '8px', cursor: 'grab',
            }} >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                    {score && (() => {
                        const sc = getScoreColor(score.score);
                        return (
                            <Tooltip arrow placement='top' title={
                                <Box sx={{ fontSize: '10px' }}>
                                    {getScoreExplanation(score).map((line, i) => (
                                        <Box key={i} sx={{ fontWeight: i === 0 ? 700 : 400, mb: i === 0 ? 0.5 : 0 }}>{line}</Box>
                                    ))}
                                </Box>
                            }>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '24px', height: '20px', borderRadius: '10px', background: sc.bg, border: `1px solid ${sc.border}`, mr: 0.5, mt: '1px', cursor: 'pointer' }}>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 800, color: sc.color }}>{score.score}</Typography>
                                </Box>
                            </Tooltip>
                        );
                    })()}
                    <Typography
                        onClick={(e) => { e.stopPropagation(); onOpen({ account }); }}
                        sx={{ fontWeight: 700, flex: 1, fontSize: '13px', lineHeight: 1.3, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    >
                        {name}
                    </Typography>
                    <IconButton size='small' onClick={(e) => { e.stopPropagation(); onOpen({ account, newWindow: true }); }} sx={{ color: grey[400], '&:hover': { color: grey[700] }, ml: 0.5 }}>
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: '11px' }} />
                    </IconButton>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, mt: 0.5 }}>
                    {[
                        { label: 'Contract', value: lastContract ? dayjs.duration((lastContract.created - Date.now()) / 1000, 'seconds').humanize(true) : null },
                        { label: 'Quote', value: lastOneOff ? dayjs.duration((lastOneOff.created - Date.now()) / 1000, 'seconds').humanize(true) : null },
                        { label: 'Update', value: lastUpdate ? dayjs.duration((lastUpdate.created - Date.now()) / 1000, 'seconds').humanize(true) : null },
                    ].filter(r => r.value).map(r => (
                        <Box key={r.label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>{r.label}</Typography>
                            <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#334155' }}>{r.value}</Typography>
                        </Box>
                    ))}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', '&:hover': { color: '#64748b' } }}
                        onClick={(e) => { e.stopPropagation(); setOwnerAnchor(e.currentTarget); }}
                    >
                        <FontAwesomeIcon icon={faUser} style={{ fontSize: '9px', color: ownerSaving ? '#3b82f6' : '#94a3b8' }} />
                        <Typography sx={{ fontSize: '10px', color: ownerSaving ? '#3b82f6' : '#94a3b8' }}>
                            {ownerSaving ? 'Saving...' : (ownerShort || 'Unassigned')}
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '9px', fontWeight: 600, color: activityColor }}>
                        {activityLabel}
                    </Typography>
                </Box>
                <Popover
                    open={Boolean(ownerAnchor)}
                    anchorEl={ownerAnchor}
                    onClose={() => setOwnerAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <MenuList dense sx={{ py: 0.5 }}>
                        {!isOwner && (
                            <MenuItem onClick={onTakeOwnership}>
                                <ListItemIcon><FontAwesomeIcon icon={faHandHoldingHeart} style={{ fontSize: '13px' }} /></ListItemIcon>
                                <ListItemText><Typography sx={{ fontSize: '12px' }}>Take Ownership</Typography></ListItemText>
                            </MenuItem>
                        )}
                        <MenuItem onClick={() => { setOwnerAnchor(null); setNewOwner(account.owner || org?.users?.[0]?.email); setChangeOwnerDialog(true); }}>
                            <ListItemIcon><FontAwesomeIcon icon={faUserPen} style={{ fontSize: '13px' }} /></ListItemIcon>
                            <ListItemText><Typography sx={{ fontSize: '12px' }}>{isOwner ? 'Change Ownership' : 'Reassign Ownership'}</Typography></ListItemText>
                        </MenuItem>
                    </MenuList>
                </Popover>
                {changeOwnerDialog && (
                    <Dialog open={true} onClose={() => setChangeOwnerDialog(false)} onClick={(e) => e.stopPropagation()}>
                        <DialogTitle>Change Ownership</DialogTitle>
                        <DialogContent>
                            <Typography variant='body2' sx={{ mt: 1, mb: 2 }}>
                                Select the new owner for this account:
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                                <OwnerFilter
                                    org={org}
                                    filter={{ members: [newOwner] }}
                                    setFilter={(filter) => setNewOwner(filter.members[0])}
                                    me={me}
                                    singlePick={true}
                                    disabled={false}
                                    teamLevel={false}
                                    noSelectionText={null}
                                    noMarginRight={false}
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setChangeOwnerDialog(false)} disabled={ownerSaving}>Cancel</Button>
                            <Button onClick={onChangeOwnership} disabled={ownerSaving || !newOwner}>
                                {ownerSaving ? <CircularProgress size={14} /> : 'Change Ownership'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
                {reminders?.filter(r => r.status === 'open').filter(r => showOverdue || !r.dueDate || new Date(r.dueDate) >= new Date()).length > 0 && (
                    <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                        {reminders.filter(r => r.status === 'open').filter(r => showOverdue || !r.dueDate || new Date(r.dueDate) >= new Date()).map(r => {
                            const isOverdue = r.dueDate && new Date(r.dueDate) < new Date();
                            return (
                                <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                    <Checkbox
                                        size='small'
                                        checked={false}
                                        sx={{ p: 0, color: isOverdue ? red[400] : grey[400] }}
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            if (!updateAccount) return;
                                            const newReminders = (account.reminders || []).map(rem => rem.id === r.id ? { ...rem, status: 'completed' } : rem);
                                            await updateAccount({ account: { ...account, reminders: newReminders }, setFirst: true });
                                        }}
                                    />
                                    <Typography sx={{ fontSize: '10px', color: isOverdue ? red[600] : '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</Typography>
                                </Box>
                            );
                        })}
                    </Box>
                )}
                {tags?.filter(t => !t.label.startsWith('Pipeline:')).length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 0.5, gap: '2px' }}>
                        {tags.filter(t => !t.label.startsWith('Pipeline:')).map((t, index) => (
                            <Chip key={index} size='small' label={t.label} sx={{ fontSize: '9px', height: '18px' }} />
                        ))}
                    </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    {saving === 'working' && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant='caption' sx={{ marginRight: '6px' }}>
                                Saving...
                            </Typography>
                            <CircularProgress size={18} sx={{ opacity: 0.5 }} />
                        </Box>
                    )}
                    {saving === 'success' && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FontAwesomeIcon icon={faCheck} color={green[500]} />
                        </Box>
                    )}
                </Box>
                {saving === 'error' && (
                    <Alert severity='error' sx={{ display: 'flex', alignItems: 'center' }}>
                        Saving failed.
                    </Alert>
                )}
            </Paper>
        </Box>
    );
}
